// src/store/vulnerabilityScanSlice.ts
import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { tenantService } from "@/src/api/api";

const extractDetailMessage = (detail: any, fallback: string): string => {
  if (typeof detail === "object" && detail !== null) {
    return typeof detail.message === "string" ? detail.message : fallback;
  }
  return typeof detail === "string" ? detail : fallback;
};

export interface VulnerabilityScanConfig {
  target: string | string[];
  ports: string;
  scanType: string;
  scanDepth: number;
  internal: boolean;
  enableCVE: boolean;
  enableCMS: boolean;
  enableZeroDay: boolean;
  throttling: number;
  timeout: number;
  authCredentials: {
    [key: string]: {
      authType: string;
      loginUrl?: string;
      usernameKey?: string;
      passwordKey?: string;
      username?: string;
      password?: string;
      token?: string;
      cookies?: { key: string; value: string }[];
    }
  } | null;
}

export interface ScannerOption {
  id: string;
  name: string;
  enabled: boolean;
}

export interface ScanSummary {
  target: string;
  scanDate: string;
  duration?: string;
  totalVulnerabilities: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  scanStatus?: string;
}

export interface Vulnerability {
  id: string;
  title: string;
  severity: string;
  description: string;
  cve?: string;
  [key: string]: any;
}

export interface CopilotFindingContext {
  finding_id?: string;
  title?: string;
  endpoint?: string;
  severity?: string;
  confidence?: number;
  target?: string;
  metadata?: Record<string, any>;
}

export interface CopilotMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  findingContext?: CopilotFindingContext | null;
}

export interface AttackPathStep {
  node_id: string;
  title: string;
  finding_id?: string;
  endpoint?: string;
  evidence?: string[];
}

export interface AttackPathItem {
  path_id: string;
  name: string;
  path_probability: number;
  confidence_reasoning: string;
  steps: AttackPathStep[];
  terminal_impact?: string | null;
}

export interface AttackPathGraphNode {
  node_id: string;
  finding_id?: string;
  title: string;
  node_type: string;
  severity?: string;
  endpoint?: string;
  target?: string;
  evidence?: string[];
}

export interface AttackPathGraphEdge {
  source_node_id: string;
  target_node_id: string;
  relationship: string;
  reasoning: string;
  probability: number;
  evidence?: string[];
}

export interface AttackPathGeneration {
  generation_id: string;
  thread_id: string;
  scan_id: string;
  risk_score: number;
  risk_reasoning: string;
  sorting: string;
  paths: AttackPathItem[];
  graph: {
    nodes: AttackPathGraphNode[];
    edges: AttackPathGraphEdge[];
  };
  har_s3_url?: string | null;
}

export interface ReportBranding {
  report_title: string;
  client_name: string;
  scan_scope: string;
  generated_at: string;
  report_id: string;
  template_id: string;
  template_version: string;
}

export interface ReportSectionSummary {
  title: string;
  subtitle?: string | null;
}

export interface ReportImpactItem {
  finding_id: string;
  finding_name: string;
  business_impact: string;
  dpdp_section?: string | null;
  iso27001_controls: string[];
  cert_in_reportable: boolean;
  financial_risk: "high" | "medium" | "low";
  remediation_urgency: "immediate" | "30_days" | "90_days";
}

export interface ReportRemediationItem {
  finding_id: string;
  finding_name: string;
  summary: string;
  steps: string[];
  code_example: string;
  effort_estimate: "1_day" | "1_week" | "1_sprint";
  priority: number;
}

export interface ReportPayload {
  branding: ReportBranding;
  section_summary: ReportSectionSummary[];
  attack_narrative: string;
  business_impact: ReportImpactItem[];
  remediation_steps: ReportRemediationItem[];
  generated_at: string;
  generation_id: string;
  scan_id: string;
  source_hash: string;
  pdf_url?: string | null;
  docx_url?: string | null;
}

export interface ReportGenerationState {
  generation_id: string;
  scan_id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  job_id?: string | null;
  payload?: ReportPayload | null;
  error?: string | null;
}

export interface OpenPort {
  port: number;
  service: string;
  state: string;
  version?: string;
  [key: string]: any;
}

export interface ScanResults {
  summary: ScanSummary;
  vulnerabilities: Vulnerability[];
  openPorts: OpenPort[];
  technologies?: string[];
}

export interface VulnerabilityScanState {
  // Scan Configuration
  scanConfig: VulnerabilityScanConfig;
  scannerOptions: ScannerOption[];
  selectedEngines: string[];

  // Auth mode — null = none, "auth" = Use Authentication, "2mfa" = Use 2MFA
  authMode: "auth" | "2mfa" | null;

  // Scan Progress & Status
  isScanning: boolean;
  scanProgress: number;
  scanStatus: string;
  scanError: string | null;

  // Scan Data & Results
  scanData: any | null;
  scanComplete: boolean;
  actualScanResults: ScanResults | null;
  showResults: boolean;

  // History & Navigation
  isHistoryView: boolean;
  currentScanId: string | null;
  activeTab: string;

  // Interceptor (DAST) session
  interceptorSession: {
    session_id: string;
    status: string;
    target: string;
    authenticated: boolean;
    interceptor_mode?: "manual" | "live";
    message?: string;
    [key: string]: any;
  } | null;
  interceptorSessionLoading: boolean;

  // Interceptor live state (survives route changes)
  interceptorIsIntercepting: boolean;
  interceptorIsPaused: boolean;
  interceptorStatus:
    | "idle"
    | "connecting"
    | "open"
    | "closing"
    | "closed"
    | "error";
  interceptorError: string | null;
  interceptorRequests: {
    id: string;
    type: string;
    url: string;
    method: string;
    headers: Record<string, string>;
    body: string;
    receivedAt: number;
  }[];
  interceptorPausedRequest: {
    id: string;
    type: string;
    url: string;
    method: string;
    headers: Record<string, string>;
    body: string;
    receivedAt: number;
  } | null;
  interceptorAiThinking: boolean;
  interceptorAiSuggestion: any | null;

  // Download & Export
  isDownloading: boolean;

  // Pagination
  pagination: {
    Page: number;
    PageSize: number;
    total_pages: number;
    total_items: number;
  };

  // Additional Metadata
  abortController: AbortController | null;
  progressInterval: NodeJS.Timeout | null;

  // Copilot context
  copilotConversationId: string | null;
  copilotMessages: CopilotMessage[];
  copilotLoading: boolean;
  copilotError: string | null;

  // Attack paths context
  attackPathsLoading: boolean;
  attackPathsError: string | null;
  attackPathsCurrentGeneration: AttackPathGeneration | null;
  attackPathsSelectedPathIds: string[];
  attackPathsViewMode: "critical" | "all" | "asset";

  // Report context
  reportLoading: boolean;
  reportError: string | null;
  reportCurrentGeneration: ReportGenerationState | null;
  reportJobId: string | null;
  reportJobStatus: "idle" | "queued" | "running" | "succeeded" | "failed";
}

const defaultScanConfig: VulnerabilityScanConfig = {
  target: "",
  ports: "",
  scanType: "full",
  scanDepth: 1,
  internal: false,
  enableCVE: true,
  enableCMS: true,
  enableZeroDay: true,
  throttling: 0,
  timeout: 300,
  authCredentials: null,
};

export const startVulnerabilityScan = createAsyncThunk(
  "vulnerabilityScan/startScan",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await tenantService.startVSScan(payload);

      // Handle API-level failure (200 but success: false)
      if (response && response.success === false) {
        const errorMessage =
          typeof response.message === "string"
            ? response.message
            : response.message?.message || "Scan failed";

        return rejectWithValue(errorMessage);
      }

      // Handle detail as string (rare case when API returns 200 with error)
      if (typeof response?.detail === "string") {
        return rejectWithValue(response.detail);
      }

      const successfulScans = response?.results?.successful_scans;
      const failedScans = response?.results?.failed_scans;

      if (
        Array.isArray(successfulScans) &&
        successfulScans.length === 0 &&
        Array.isArray(failedScans) &&
        failedScans.length > 0
      ) {
        return rejectWithValue({
          message: "Scan failed. Please check the target and try again.",
        });
      }

      if (response?.results?.batch_summary?.scan_id) {
        return response;
      }

      return rejectWithValue({ message: "No scan data received" });

    } catch (error: any) {
      const detail = error?.response?.data?.detail;

      // object case (your TENANT_INACTIVE)
      if (typeof detail === "object" && detail !== null) {
        return rejectWithValue(detail.message || "Scan failed");
      }

      // string case
      if (typeof detail === "string") {
        return rejectWithValue(detail);
      }

      return rejectWithValue(
        typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : error?.message || "Failed to start scan"
      );
    }
  }
);

// Thunk for Basic Authentication scan (calls /start/scan/Auth-batch)
export const startVulnerabilityScanWithAuth = createAsyncThunk(
  "vulnerabilityScan/startScanWithAuth",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await tenantService.startVsAuthScan(payload);

      if (response && response.success === false) {
        return rejectWithValue(response.message || "Scan failed");
      }

      if (response?.detail) {
        return rejectWithValue(extractDetailMessage(response.detail, "Scan failed"));
      }

      const successfulScans = response?.results?.successful_scans;
      const failedScans = response?.results?.failed_scans;

      if (
        Array.isArray(successfulScans) &&
        successfulScans.length === 0 &&
        Array.isArray(failedScans) &&
        failedScans.length > 0
      ) {
        return rejectWithValue(
          "Scan failed. Please check the target and try again."
        );
      }

      if (response?.results && response?.results?.batch_summary?.scan_id) {
        return response;
      } else {
        return rejectWithValue("No scan data received");
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      return rejectWithValue(
        extractDetailMessage(detail, error?.response?.data?.message || error.message || "Failed to start authenticated scan")
      );
    }
  }
);

// Thunk for Web Scan without Authentication (calls /start/scan/unAuth-batch)
export const startVulnerabilityScanUnAuth = createAsyncThunk(
  "vulnerabilityScan/startScanUnAuth",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await tenantService.startVsUnAuthScan(payload);

      if (response && response.success === false) {
        return rejectWithValue(response.message || "Scan failed");
      }

      if (response?.detail) {
        return rejectWithValue(extractDetailMessage(response.detail, "Scan failed"));
      }

      const successfulScans = response?.results?.successful_scans;
      const failedScans = response?.results?.failed_scans;

      if (
        Array.isArray(successfulScans) &&
        successfulScans.length === 0 &&
        Array.isArray(failedScans) &&
        failedScans.length > 0
      ) {
        return rejectWithValue(
          "Scan failed. Please check the target and try again."
        );
      }

      if (response?.results && response?.results?.batch_summary?.scan_id) {
        return response;
      } else {
        return rejectWithValue("No scan data received");
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      return rejectWithValue(
        extractDetailMessage(detail, error?.response?.data?.message || error.message || "Failed to start unauthenticated scan")
      );
    }
  }
);

// Thunk for Network/Combined Scan without Authentication (calls /start/scan/combined-batch/unauth)
export const startCombinedScanUnAuth = createAsyncThunk(
  "vulnerabilityScan/startCombinedScanUnAuth",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await tenantService.startVsCombinedUnAuthScan(payload);

      if (response && response.success === false) {
        return rejectWithValue(response.message || "Scan failed");
      }

      if (response?.detail) {
        return rejectWithValue(extractDetailMessage(response.detail, "Scan failed"));
      }

      const successfulScans = response?.results?.successful_scans;
      const failedScans = response?.results?.failed_scans;

      if (
        Array.isArray(successfulScans) &&
        successfulScans.length === 0 &&
        Array.isArray(failedScans) &&
        failedScans.length > 0
      ) {
        return rejectWithValue(
          "Scan failed. Please check the target and try again."
        );
      }

      if (response?.results && response?.results?.batch_summary?.scan_id) {
        return response;
      } else {
        return rejectWithValue("No scan data received");
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      return rejectWithValue(
        extractDetailMessage(detail, error?.response?.data?.message || error.message || "Failed to start combined scan")
      );
    }
  }
);

// Thunk for Combined Scan with Authentication (calls /start/scan/combined-batch/auth)
export const startCombinedScanAuth = createAsyncThunk(
  "vulnerabilityScan/startCombinedScanAuth",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await tenantService.startVsCombinedAuthScan(payload);

      if (response && response.success === false) {
        return rejectWithValue(response.message || "Scan failed");
      }

      if (response?.detail) {
        return rejectWithValue(extractDetailMessage(response.detail, "Scan failed"));
      }

      const successfulScans = response?.results?.successful_scans;
      const failedScans = response?.results?.failed_scans;

      if (
        Array.isArray(successfulScans) &&
        successfulScans.length === 0 &&
        Array.isArray(failedScans) &&
        failedScans.length > 0
      ) {
        return rejectWithValue(
          "Scan failed. Please check the target and try again."
        );
      }

      if (response?.results && response?.results?.batch_summary?.scan_id) {
        return response;
      } else {
        return rejectWithValue("No scan data received");
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      return rejectWithValue(
        extractDetailMessage(detail, error?.response?.data?.message || error.message || "Failed to start authenticated combined scan")
      );
    }
  }
);

// Thunk for Network Scan (calls /start/scan/network-batch)
export const startNetworkScanBatch = createAsyncThunk(
  "vulnerabilityScan/startNetworkScanBatch",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await tenantService.startVsNetworkBatchScan(payload);

      if (response && response.success === false) {
        return rejectWithValue(response.message || "Scan failed");
      }

      if (response?.detail) {
        return rejectWithValue(extractDetailMessage(response.detail, "Scan failed"));
      }

      const successfulScans = response?.results?.successful_scans;
      const failedScans = response?.results?.failed_scans;

      if (
        Array.isArray(successfulScans) &&
        successfulScans.length === 0 &&
        Array.isArray(failedScans) &&
        failedScans.length > 0
      ) {
        return rejectWithValue(
          "Scan failed. Please check the target and try again."
        );
      }

      if (response?.results && response?.results?.batch_summary?.scan_id) {
        return response;
      } else {
        return rejectWithValue("No scan data received");
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      return rejectWithValue(
        extractDetailMessage(detail, error?.response?.data?.message || error.message || "Failed to start network scan")
      );
    }
  }
);

export const sendCopilotMessage = createAsyncThunk(
  "vulnerabilityScan/sendCopilotMessage",
  async (
    payload: {
      scan_id: string;
      message: string;
      conversation_id?: string | null;
      finding_context?: CopilotFindingContext | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await tenantService.copilotChat({
        scan_id: payload.scan_id,
        message: payload.message,
        conversation_id: payload.conversation_id || undefined,
        finding_context: payload.finding_context || undefined,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to send copilot message"
      );
    }
  }
);

const initialState: VulnerabilityScanState = {
  // Scan Configuration
  scanConfig: defaultScanConfig,
  scannerOptions: [
    { id: "nmap", name: "Network Scanning", enabled: false },
    { id: "web_application_scan", name: "Web Scanning", enabled: true },
  ],
  selectedEngines: ["web_application_scan"],

  authMode: null, // "auth" | "2mfa" | null

  // Scan Progress & Status
  isScanning: false,
  scanProgress: 0,
  scanStatus: "Initializing scan...",
  scanError: null,

  // Scan Data & Results
  scanData: null,
  scanComplete: false,
  actualScanResults: null,
  showResults: false,

  // History & Navigation
  isHistoryView: false,
  currentScanId: null,
  activeTab: "config",

  // Interceptor (DAST) session
  interceptorSession: null,
  interceptorSessionLoading: false,
  interceptorIsIntercepting: false,
  interceptorIsPaused: false,
  interceptorStatus: "idle",
  interceptorError: null,
  interceptorRequests: [],
  interceptorPausedRequest: null,
  interceptorAiThinking: false,
  interceptorAiSuggestion: null,

  // Download & Export
  isDownloading: false,

  // Pagination
  pagination: {
    Page: 1,
    PageSize: 5,
    total_pages: 1,
    total_items: 0,
  },

  // Additional Metadata
  abortController: null,
  progressInterval: null,

  // Copilot context
  copilotConversationId: null,
  copilotMessages: [],
  copilotLoading: false,
  copilotError: null,

  // Attack paths context
  attackPathsLoading: false,
  attackPathsError: null,
  attackPathsCurrentGeneration: null,
  attackPathsSelectedPathIds: [],
  attackPathsViewMode: "all",
  reportLoading: false,
  reportError: null,
  reportCurrentGeneration: null,
  reportJobId: null,
  reportJobStatus: "idle",
};

const vulnerabilityScanSlice = createSlice({
  name: "vulnerabilityScan",
  initialState,
  reducers: {
    // Configuration Updates
    setScanConfig: (state, action: PayloadAction<Partial<VulnerabilityScanConfig>>) => {
      state.scanConfig = { ...state.scanConfig, ...action.payload };
    },
    
    setScannerOptions: (state, action: PayloadAction<ScannerOption[]>) => {
      state.scannerOptions = action.payload;
    },

    setSelectedEngines: (state, action: PayloadAction<string[]>) => {
      state.selectedEngines = action.payload;
    },

    // Scan Progress & Status
    setIsScanning: (state, action: PayloadAction<boolean>) => {
      state.isScanning = action.payload;
    },

    setScanProgress: (state, action: PayloadAction<number>) => {
      state.scanProgress = action.payload;
    },

    setScanStatus: (state, action: PayloadAction<string>) => {
      state.scanStatus = action.payload;
    },

    setScanError: (state, action: PayloadAction<string | null>) => {
      state.scanError = action.payload;
    },

    // Scan Data & Results
    setScanData: (state, action: PayloadAction<any>) => {
      state.scanData = action.payload;
    },

    setScanComplete: (state, action: PayloadAction<boolean>) => {
      state.scanComplete = action.payload;
    },

    setActualScanResults: (state, action: PayloadAction<ScanResults | null>) => {
      state.actualScanResults = action.payload;
    },

    setShowResults: (state, action: PayloadAction<boolean>) => {
      state.showResults = action.payload;
    },

    // History & Navigation
    setIsHistoryView: (state, action: PayloadAction<boolean>) => {
      state.isHistoryView = action.payload;
    },

    setCurrentScanId: (state, action: PayloadAction<string | null>) => {
      state.currentScanId = action.payload;
    },

    setInterceptorSession: (
      state,
      action: PayloadAction<VulnerabilityScanState["interceptorSession"]>
    ) => {
      state.interceptorSession = action.payload;
    },

    setInterceptorSessionLoading: (state, action: PayloadAction<boolean>) => {
      state.interceptorSessionLoading = action.payload;
    },

    setInterceptorIsIntercepting: (state, action: PayloadAction<boolean>) => {
      state.interceptorIsIntercepting = action.payload;
    },

    setInterceptorIsPaused: (state, action: PayloadAction<boolean>) => {
      state.interceptorIsPaused = action.payload;
    },

    setInterceptorStatus: (
      state,
      action: PayloadAction<VulnerabilityScanState["interceptorStatus"]>
    ) => {
      state.interceptorStatus = action.payload;
    },

    setInterceptorError: (state, action: PayloadAction<string | null>) => {
      state.interceptorError = action.payload;
    },

    appendInterceptorRequest: (
      state,
      action: PayloadAction<VulnerabilityScanState["interceptorRequests"][number]>
    ) => {
      state.interceptorRequests.push(action.payload);
      if (action.payload.type === "paused") {
        state.interceptorPausedRequest = action.payload;
      } else {
        state.interceptorPausedRequest = null;
      }
    },

    setInterceptorPausedRequest: (
      state,
      action: PayloadAction<VulnerabilityScanState["interceptorPausedRequest"]>
    ) => {
      state.interceptorPausedRequest = action.payload;
    },

    setInterceptorAiThinking: (state, action: PayloadAction<boolean>) => {
      state.interceptorAiThinking = action.payload;
    },

    setInterceptorAiSuggestion: (state, action: PayloadAction<any | null>) => {
      state.interceptorAiSuggestion = action.payload;
      if (action.payload) {
        state.interceptorAiThinking = false;
      }
    },

    clearInterceptorRequests: (state) => {
      state.interceptorRequests = [];
      state.interceptorPausedRequest = null;
      state.interceptorAiThinking = false;
      state.interceptorAiSuggestion = null;
    },

    resetInterceptorRuntime: (state) => {
      state.interceptorSessionLoading = false;
      state.interceptorIsIntercepting = false;
      state.interceptorIsPaused = false;
      state.interceptorStatus = "idle";
      state.interceptorError = null;
      state.interceptorRequests = [];
      state.interceptorPausedRequest = null;
      state.interceptorAiThinking = false;
      state.interceptorAiSuggestion = null;
    },

    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },

    // Download & Export
    setIsDownloading: (state, action: PayloadAction<boolean>) => {
      state.isDownloading = action.payload;
    },

    // Pagination
    setPagination: (state, action: PayloadAction<Partial<typeof state.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // Abort & Progress Control
    setAbortController: (state, action: PayloadAction<AbortController | null>) => {
      state.abortController = action.payload;
    },

    setProgressInterval: (state, action: PayloadAction<NodeJS.Timeout | null>) => {
      state.progressInterval = action.payload;
    },

    // Copilot state
    setCopilotConversationId: (state, action: PayloadAction<string | null>) => {
      state.copilotConversationId = action.payload;
    },
    addCopilotMessage: (state, action: PayloadAction<CopilotMessage>) => {
      state.copilotMessages.push(action.payload);
    },
    clearCopilotMessages: (state) => {
      state.copilotMessages = [];
      state.copilotError = null;
    },
    setCopilotConversation: (
      state,
      action: PayloadAction<{
        conversationId: string | null;
        messages: CopilotMessage[];
      }>
    ) => {
      state.copilotConversationId = action.payload.conversationId;
      state.copilotMessages = action.payload.messages;
      state.copilotError = null;
    },
    setCopilotLoading: (state, action: PayloadAction<boolean>) => {
      state.copilotLoading = action.payload;
    },
    setCopilotError: (state, action: PayloadAction<string | null>) => {
      state.copilotError = action.payload;
    },
    setAttackPathsLoading: (state, action: PayloadAction<boolean>) => {
      state.attackPathsLoading = action.payload;
    },
    setAttackPathsError: (state, action: PayloadAction<string | null>) => {
      state.attackPathsError = action.payload;
    },
    setAttackPathsCurrentGeneration: (state, action: PayloadAction<AttackPathGeneration | null>) => {
      state.attackPathsCurrentGeneration = action.payload;
    },
    setAttackPathsSelectedPathIds: (state, action: PayloadAction<string[]>) => {
      state.attackPathsSelectedPathIds = action.payload;
    },
    setAttackPathsViewMode: (state, action: PayloadAction<"critical" | "all" | "asset">) => {
      state.attackPathsViewMode = action.payload;
    },
    setReportLoading: (state, action: PayloadAction<boolean>) => {
      state.reportLoading = action.payload;
    },
    setReportError: (state, action: PayloadAction<string | null>) => {
      state.reportError = action.payload;
    },
    setReportCurrentGeneration: (state, action: PayloadAction<ReportGenerationState | null>) => {
      state.reportCurrentGeneration = action.payload;
    },
    setReportJobId: (state, action: PayloadAction<string | null>) => {
      state.reportJobId = action.payload;
    },
    setReportJobStatus: (state, action: PayloadAction<"idle" | "queued" | "running" | "succeeded" | "failed">) => {
      state.reportJobStatus = action.payload;
    },

    // Reset Scan State
    resetVulnerabilityScanState: (state) => {
      return {
        ...initialState,
        
      };
    },

    // Reset Full State
    resetFullVulnerabilityScanState: (state) => {
      return initialState;
    },

    // Bulk Update (useful for API responses)
    updateScanState: (state, action: PayloadAction<Partial<VulnerabilityScanState>>) => {
      return { ...state, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startVulnerabilityScan.pending, (state) => {
        state.isScanning = true;
        state.scanError = null;
        state.scanStatus = "Initializing scan...";
      })
      .addCase(startVulnerabilityScan.fulfilled, (state, action) => {
        state.isScanning = false;
        state.scanData = action.payload.results;
        state.scanComplete = true;
        state.scanStatus = "Scan completed successfully!";
      })
      .addCase(startVulnerabilityScan.rejected, (state, action) => {
        state.isScanning = false;
        state.scanError = action.payload as string || action.error.message || "Scan failed";
      })
      // Auth-batch scan (Basic Authentication)
      .addCase(startVulnerabilityScanWithAuth.pending, (state) => {
        state.isScanning = true;
        state.scanError = null;
        state.scanStatus = "Initializing authenticated scan...";
      })
      .addCase(startVulnerabilityScanWithAuth.fulfilled, (state, action) => {
        state.isScanning = false;
        state.scanData = action.payload.results;
        state.scanComplete = true;
        state.scanStatus = "Scan completed successfully!";
      })
      .addCase(startVulnerabilityScanWithAuth.rejected, (state, action) => {
        state.isScanning = false;
        state.scanError = action.payload as string || action.error.message || "Authenticated scan failed";
      })
      // unAuth-batch scan (Web scan without authentication)
      .addCase(startVulnerabilityScanUnAuth.pending, (state) => {
        state.isScanning = true;
        state.scanError = null;
        state.scanStatus = "Initializing unauthenticated scan...";
      })
      .addCase(startVulnerabilityScanUnAuth.fulfilled, (state, action) => {
        state.isScanning = false;
        state.scanData = action.payload.results;
        state.scanComplete = true;
        state.scanStatus = "Scan completed successfully!";
      })
      .addCase(startVulnerabilityScanUnAuth.rejected, (state, action) => {
        state.isScanning = false;
        state.scanError = action.payload as string || action.error.message || "Unauthenticated scan failed";
      })
      // Combined-batch scan (Network/Combined without authentication)
      .addCase(startCombinedScanUnAuth.pending, (state) => {
        state.isScanning = true;
        state.scanError = null;
        state.scanStatus = "Initializing combined scan...";
      })
      .addCase(startCombinedScanUnAuth.fulfilled, (state, action) => {
        state.isScanning = false;
        state.scanData = action.payload.results;
        state.scanComplete = true;
        state.scanStatus = "Scan completed successfully!";
      })
      .addCase(startCombinedScanUnAuth.rejected, (state, action) => {
        state.isScanning = false;
        state.scanError = action.payload as string || action.error.message || "Combined scan failed";
      })
      // Combined-batch scan with authentication
      .addCase(startCombinedScanAuth.pending, (state) => {
        state.isScanning = true;
        state.scanError = null;
        state.scanStatus = "Initializing authenticated combined scan...";
      })
      .addCase(startCombinedScanAuth.fulfilled, (state, action) => {
        state.isScanning = false;
        state.scanData = action.payload.results;
        state.scanComplete = true;
        state.scanStatus = "Scan completed successfully!";
      })
      .addCase(startCombinedScanAuth.rejected, (state, action) => {
        state.isScanning = false;
        state.scanError = action.payload as string || action.error.message || "Authenticated combined scan failed";
      })
      // Network-batch scan
      .addCase(startNetworkScanBatch.pending, (state) => {
        state.isScanning = true;
        state.scanError = null;
        state.scanStatus = "Initializing network scan...";
      })
      .addCase(startNetworkScanBatch.fulfilled, (state, action) => {
        state.isScanning = false;
        state.scanData = action.payload.results;
        state.scanComplete = true;
        state.scanStatus = "Scan completed successfully!";
      })
      .addCase(startNetworkScanBatch.rejected, (state, action) => {
        state.isScanning = false;
        state.scanError = action.payload as string || action.error.message || "Network scan failed";
      })
      .addCase(sendCopilotMessage.pending, (state, action) => {
        state.copilotLoading = true;
        state.copilotError = null;
        const arg = action.meta.arg;
        state.copilotMessages.push({
          role: "user",
          content: arg.message,
          timestamp: new Date().toISOString(),
          findingContext: arg.finding_context || null,
        });
      })
      .addCase(sendCopilotMessage.fulfilled, (state, action) => {
        state.copilotLoading = false;
        state.copilotConversationId = action.payload?.conversation_id || state.copilotConversationId;
        state.copilotMessages.push({
          role: "assistant",
          content: action.payload?.answer || "",
          timestamp: new Date().toISOString(),
        });
      })
      .addCase(sendCopilotMessage.rejected, (state, action) => {
        state.copilotLoading = false;
        state.copilotError = action.payload as string || action.error.message || "Copilot failed";
      });
  },
});

export const {
  setScanConfig,
  setScannerOptions,
  setSelectedEngines,
  setIsScanning,
  setScanProgress,
  setScanStatus,
  setScanError,
  setScanData,
  setScanComplete,
  setActualScanResults,
  setShowResults,
  setIsHistoryView,
  setCurrentScanId,
  setInterceptorSession,
  setInterceptorSessionLoading,
  setInterceptorIsIntercepting,
  setInterceptorIsPaused,
  setInterceptorStatus,
  setInterceptorError,
  appendInterceptorRequest,
  setInterceptorPausedRequest,
  setInterceptorAiThinking,
  setInterceptorAiSuggestion,
  clearInterceptorRequests,
  resetInterceptorRuntime,
  setActiveTab,
  setIsDownloading,
  setPagination,
  setAbortController,
  setProgressInterval,
  setCopilotConversationId,
  addCopilotMessage,
  clearCopilotMessages,
  setCopilotConversation,
  setCopilotLoading,
  setCopilotError,
  setAttackPathsLoading,
  setAttackPathsError,
  setAttackPathsCurrentGeneration,
  setAttackPathsSelectedPathIds,
  setAttackPathsViewMode,
  setReportLoading,
  setReportError,
  setReportCurrentGeneration,
  setReportJobId,
  setReportJobStatus,
  resetVulnerabilityScanState,
  resetFullVulnerabilityScanState,
  updateScanState,
} = vulnerabilityScanSlice.actions;

export default vulnerabilityScanSlice.reducer;
